import React, { useState, useCallback } from 'react';
import { PageLayout } from '@/shared/layout/PageLayout';
import { Button, Card, Badge, Modal, EmptyState } from '@/components/ui';
import { useCompanies } from './hooks/useCompanies';
import { PLAN_CONFIG } from './types';
import type { Company, SubscriptionPlanCode } from './types';
import { useNavigate } from 'react-router-dom';
import { useMergedPermissions } from '@/core/permissions/hooks/useMergedPermissions';
import { CompanyLockModal } from './components/CompanyLockModal';
import CompanyFormModal from './components/CompanyFormModal';
import { CompaniesDesktopTable } from './components/CompaniesDesktopTable';
import { CompaniesMobileList } from './components/CompaniesMobileList';
import { CompanyApprovalModal } from './components/CompanyApprovalModal';
import { Badge as UiBadge } from '@/components/ui';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';
import { companiesService } from './services/companies.service';

export default function CompaniesPage() {
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'created_at'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [lockingId, setLockingId] = useState<string | null>(null);
  const [editingCompany, setEditingCompany] = useState<Company | null | undefined>(undefined);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const { can, roleCode: currentUserRole } = useMergedPermissions();
  const { impersonateUser } = useAuth();

  const handleImpersonate = async (id: string) => {
    try {
      const { data: profile, error } = await companiesService.getOwnerProfile(id);
      if (error || !profile) {
        toast.error('Không tìm thấy tài khoản quản trị của Tenant này.');
        return;
      }
      await impersonateUser(profile);
      toast.success('Đã nhập vai thành công!');
      navigate('/');
    } catch (err: any) {
      toast.error('Không thể nhập vai: ' + err.message);
    }
  };

  const {
    companies,
    isLoading,
    deleteCompany,
    isDeleting,
    upsertCompany,
    isSaving,
    lockCompany,
    unlockCompany,
    activateCompany,
    isLocking,
    isUnlocking,
    isActivating
  } = useCompanies({
    search: search || undefined,
    plan: planFilter || undefined
  });

  const navigate = useNavigate();

  // Sort logic (can be moved to hook if needed)
  const sortedCompanies = [...companies].sort((a, b) => {
    const v1 = a[sortBy] ?? '';
    const v2 = b[sortBy] ?? '';
    if (v1 < v2) return sortOrder === 'asc' ? -1 : 1;
    if (v1 > v2) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const handleExportCSV = () => {
    const headers = ['ID', 'Mã', 'Tên', 'Email', 'Gói', 'Trạng thái'];
    const rows = sortedCompanies.map(c => [c.id, c.code, c.name, c.email || '', c.subscription_plan_code, c.is_active ? 'Active' : 'Inactive']);
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `companies_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const toolbarProps = {
    searchValue: search,
    onSearchChange: setSearch,
    searchPlaceholder: "Truy vấn Tenant, Mã hệ thống hoặc Email...",
    className: "mb-8",
    primaryAction: (
      <AuthButton
        module="companies"
        action="create"
        variant="primary"
        icon="ri-add-line"
        onClick={() => setEditingCompany(null)}
        className="h-12 lg:h-14 px-8 rounded-2xl font-black tracking-widest text-[11px] shadow-xl shadow-brand-500/25 border-none"
      >
        Khởi tạo Tenant
      </AuthButton>
    ),
    extraLeft: (
      <div className="flex items-center gap-3">
        <div className="relative group">
          <i className="ri-filter-3-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-brand-600 transition-colors" />
          <select
            value={planFilter}
            onChange={e => setPlanFilter(e.target.value)}
            className="h-12 lg:h-14 pl-11 pr-10 bg-white border border-slate-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-brand-500/10 shadow-sm text-xs font-black text-slate-700 appearance-none cursor-pointer"
          >
            <option value="">TẤT CẢ TIER</option>
            <option value="free">STANDARD FREE</option>
            <option value="pro">ENTERPRISE PRO</option>
            <option value="enterprise">UNLIMITED ELITE</option>
          </select>
          <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>

        <div className="flex items-center gap-1 bg-white border border-slate-100 rounded-2xl p-1 h-12 lg:h-14 shadow-sm">
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value as any)}
            className="h-full px-4 text-xs bg-transparent cursor-pointer focus:outline-none font-black text-slate-500  tracking-tighter"
          >
            <option value="name">Sắp xếp: Tên</option>
            <option value="created_at">Sắp xếp: Ngày tạo</option>
          </select>
          <div className="w-[1px] h-6 bg-slate-100 mx-1" />
          <button
            onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
            className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-slate-50 text-slate-400 cursor-pointer transition-all hover:text-brand-600"
            title={sortOrder === 'asc' ? 'Tăng dần' : 'Giảm dần'}
          >
            <i className={sortOrder === 'asc' ? 'ri-sort-asc' : 'ri-sort-desc'} />
          </button>
        </div>
      </div>
    )
  };

  const stats = {
    total: companies.length,
    active: companies.filter(c => c.is_active).length,
    pro: companies.filter(c => c.subscription_plan_code === 'pro').length,
    enterprise: companies.filter(c => c.subscription_plan_code === 'enterprise').length,
  };

  const kpiSection = (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
      {[
        { label: 'Tổng công ty', value: stats.total, icon: 'ri-building-4-line', trend: '+2', color: 'text-blue-600', bg: 'bg-blue-50', shadow: 'shadow-blue-500/10' },
        { label: 'Đang hoạt động', value: stats.active, icon: 'ri-checkbox-circle-line', trend: '98%', color: 'text-emerald-600', bg: 'bg-emerald-50', shadow: 'shadow-emerald-500/10' },
        { label: 'Gói Premium', value: stats.pro + stats.enterprise, icon: 'ri-vip-crown-line', trend: '+12%', color: 'text-amber-600', bg: 'bg-amber-50', shadow: 'shadow-amber-500/10' },
        { label: 'Dự án hệ thống', value: companies.reduce((acc, c) => acc + (c.current_projects || 0), 0), icon: 'ri-stack-line', trend: 'Active', color: 'text-indigo-600', bg: 'bg-indigo-50', shadow: 'shadow-indigo-500/10' },
      ].map(kpi => (
        <Card key={kpi.label} className={`p-6 border-none shadow-xl ${kpi.shadow} relative overflow-hidden group hover:-translate-y-1 transition-all duration-300`}>
          <div className="flex items-center justify-between relative z-10">
            <div>
              <p className="text-[10px] font-black text-slate-400  tracking-widest mb-1">{kpi.label}</p>
              <h3 className="text-3xl font-black text-slate-900 tracking-tight">{kpi.value}</h3>
              <div className="flex items-center gap-1.5 mt-2">
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${kpi.bg} ${kpi.color}`}>
                  {kpi.trend}
                </span>
                <span className="text-[10px] text-slate-400 font-medium italic">vs last month</span>
              </div>
            </div>
            <div className={`w-14 h-14 flex items-center justify-center rounded-2xl ${kpi.bg} ${kpi.color} shadow-inner`}>
              <i className={`${kpi.icon} text-2xl`} />
            </div>
          </div>
          {/* Decorative background icon */}
          <i className={`${kpi.icon} absolute -bottom-4 -right-4 text-8xl opacity-[0.03] group-hover:scale-110 transition-transform duration-500`} />
        </Card>
      ))}
    </div>
  );

  return (
    <PageLayout
      title="Tenant Command Center"
      breadcrumbs={[{ label: 'Hệ thống', path: '/' }, { label: 'Quản lý Tenant' }]}
      badge={
        <UiBadge variant="custom" className="bg-slate-900 text-amber-400 border-amber-500/50 h-7 px-4 shadow-lg shadow-amber-500/10">
          <i className="ri-vip-crown-fill mr-1.5 text-xs" />
          SYSTEM MASTER
        </UiBadge>
      }
      description="Quản lý tập trung các tổ chức, tài nguyên và cấu hình multi-tenant cấp cao."
      headerActions={
        <Button variant="secondary" icon="ri-download-line" onClick={handleExportCSV}>
          Xuất CSV
        </Button>
      }
      beforeToolbar={kpiSection}
      isLoading={isLoading}
      isEmpty={companies.length === 0}
      emptyTitle="Chưa có công ty nào"
      emptyDescription="Bắt đầu bằng cách thêm công ty mới vào hệ thống."
      {...toolbarProps}
    >
      <CompaniesDesktopTable
        companies={sortedCompanies}
        onLock={(id) => setLockingId(id)}
        onUnlock={(id) => unlockCompany(id)}
        onDelete={(id) => setDeletingId(id)}
        onEdit={(company) => setEditingCompany(company)}
        onActivate={(id) => setApprovingId(id)}
        onViewDetail={(id) => navigate(`/companies/detail/${id}`)}
        onImpersonate={handleImpersonate}
        canDelete={can}
      />

      <CompaniesMobileList
        companies={sortedCompanies}
        onLock={(id) => setLockingId(id)}
        onUnlock={(id) => unlockCompany(id)}
        onDelete={(id) => setDeletingId(id)}
        onEdit={(company) => setEditingCompany(company)}
        onActivate={(id) => setApprovingId(id)}
        onViewDetail={(id) => navigate(`/companies/detail/${id}`)}
        onImpersonate={handleImpersonate}
        canDelete={can}
      />

      <Modal
        isOpen={!!deletingId}
        onClose={() => setDeletingId(null)}
        title="Xác nhận xóa công ty"
        variant="danger"
      >
        <div className="p-6 text-center">
          <div className="w-20 h-20 bg-red-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
            <i className="ri-error-warning-fill text-red-600 text-4xl" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-3">Bạn chắc chắn muốn xóa?</h3>
          <p className="text-sm text-slate-500 mb-8 leading-relaxed px-4">
            Hành động này sẽ xóa vĩnh viễn dữ liệu công ty và ảnh hưởng đến tất cả các tenant liên quan. **Không thể hoàn tác.**
          </p>
          <div className="flex gap-4">
            <Button variant="ghost" className="flex-1 h-12" onClick={() => setDeletingId(null)}>
              Hủy bỏ
            </Button>
            <Button
              variant="danger"
              className="flex-1 h-12"
              isLoading={isDeleting}
              onClick={async () => {
                if (deletingId) {
                  await deleteCompany(deletingId);
                  setDeletingId(null);
                }
              }}
            >
              Xác nhận xóa
            </Button>
          </div>
        </div>
      </Modal>

      <CompanyLockModal
        isOpen={!!lockingId}
        onClose={() => setLockingId(null)}
        companyName={companies.find(c => c.id === lockingId)?.name || ''}
        isLoading={isLocking}
        onConfirm={async (reason) => {
          if (lockingId) {
            await lockCompany({ id: lockingId, reason });
            setLockingId(null);
          }
        }}
      />

      <CompanyFormModal
        isOpen={editingCompany !== undefined}
        onClose={() => setEditingCompany(undefined)}
        company={editingCompany}
        isLoading={isSaving}
        onSave={upsertCompany}
      />

      <CompanyApprovalModal
        isOpen={!!approvingId}
        onClose={() => setApprovingId(null)}
        company={companies.find(c => c.id === approvingId) || null}
        isLoading={isActivating}
        onConfirm={async (options) => {
          if (approvingId) {
            await activateCompany({ id: approvingId, options });
            setApprovingId(null);
          }
        }}
      />
    </PageLayout>
  );
}
